import React, { useState } from 'react';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useSdk } from '../../sdkContext';
import {
  fetchSubscriptionFinal,
  completeSubscription,
  pickupSubscription,
  initiatePaymentUpdate,
  syncSubscriptionPayment,
  cancelSubscription
} from './api';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '../../containers';
import ChangeCardForm from './ChangeCardForm';
import EditInvoiceForm from './EditInvoiceForm';
import InfoTile from './InfoTile';
import PaymentDetails from './PaymentDetails';
import InvoiceDetails from './InvoiceDetails';
import SubscriptionHero from './SubscriptionHero';
import SubscriptionHistory from './SubscriptionHistory';
import ConfirmationModal from './ConfirmationModal';
import SubscriptionChat from './SubscriptionChat';

import css from './SubscriptionPage.css';
import SubscriptionDeliveryDetails from './SubscriptionDeliveryDetails';
import CancellationNotice from './CancellationNotice';

export const SubscriptionPageComponent = props => {
  const { transactionRole, intl, pageName, params: { id } } = props;

  const sdk = useSdk();
  const queryClient = useQueryClient();
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    actionType: null
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);
  const isProvider = transactionRole === 'provider' && pageName === 'SubscriptionSaleDetailsPage';
  const currentUser = useSelector(state => state.user.currentUser)

  const openConfirmation = (type) => setConfirmationModal({ isOpen: true, actionType: type });
  const closeConfirmation = () => setConfirmationModal({ isOpen: false, actionType: null });

  const { mutate: startUpdate, isLoading: isStarting } = useMutation(
    () => initiatePaymentUpdate(sdk, id),
    {
      onSuccess: (data) => {
        setSetupIntentSecret(data.attributes.stripeSetupIntent.clientSecret);
        setIsModalOpen(true);
      },
      onError: (err) => console.error("Błąd inicjalizacji: " + err.message)
    }
  );

  const syncMutation = useMutation(
    () => syncSubscriptionPayment(sdk, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscription-page', id]);
        setIsModalOpen(false);
      },
      onError: (err) => console.error("Błąd synchronizacji: " + err.message)
    }
  );

  const { data: subscription, isLoading, error } = useQuery(
    ['subscription-page', id],
    () => fetchSubscriptionFinal(sdk, id),
    { enabled: !!id }
  );

  const { mutate: handlePickup, isLoading: isPickingUp } = useMutation(
    () => pickupSubscription(sdk, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscription-page', id]);
        closeConfirmation();
      },
      onError: (err) => {
      }
    }
  );

  const { mutate: handleComplete, isLoading: isCompleting } = useMutation(
    (data) => {
      return sdk.newSdk.subscriptions.update({
        id,
        complete: { refund: data?.refund || 'none' }
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscription-page', id]);
        closeConfirmation();
      },
      onError: (err) => console.error("Błąd zamykania: " + err.message)
    }
  );

  const updateInvoiceMutation = useMutation(
    (newInvoiceData) => sdk.newSdk.subscriptions.update({
      id,
      meta: {
        customerType: newInvoiceData.customerType,
        invoice: newInvoiceData
      }
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscription-page', id]);
        setIsInvoiceModalOpen(false);
      },
      onError: (err) => console.error("Błąd zapisu: " + err.message)
    }
  );

  const { mutate: handleCancel, isLoading: isCancelling } = useMutation(
    () => cancelSubscription(sdk, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscription-page', id]);
        closeConfirmation();
      },
      onError: (err) => console.error("Błąd anulowania: " + err.message)
    }
  );

  const { data: messages, isLoading: messagesLoading } = useQuery(
    ['subscription-messages', id],
    () => sdk.newSdk.messages.query({ context: 'subscription', subscriptionId: id }),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );

  const sendMessageMutation = useMutation(
    (text) => sdk.newSdk.messages.create({ context: 'subscription', subscriptionId: id, text }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscription-messages', id]);
      },
      onError: (err) => console.error("Nie udało się wysłać wiadomości: " + err.message)
    }
  );

  const canUserCancel = (subscription) => {
    if (!subscription) return false;

    const { state } = subscription.attributes;
    const transitions = subscription.attributes.transitions || [];

    if (state === 'completed' || state === 'cancelled') return false;

    const paymentTransitions = transitions.filter(t => t.code === 'payment');
    return paymentTransitions.length <= 1;
  };

  const listing = subscription?.listing;
  const provider = subscription?.provider;
  const { processState, state } = subscription?.attributes || {};
  const transitions = subscription?.attributes?.transitions || [];
  const paymentCount = transitions.filter(t => t.code === 'payment').length;

  const nextPaymentDate = subscription?.attributes?.nextPaymentAt ? new Date(subscription.attributes.nextPaymentAt) : null;
  const cardInfo = subscription?.attributes?.card;
  const invoiceData = subscription?.attributes?.meta?.invoice;
  const isCancelable = canUserCancel(subscription);

  const isAdmin = currentUser?.attributes?.isAdmin || currentUser?.attributes.adminForCountries.includes(c => c === provider.attributes.countryId);

  const canPickup = processState === 'accepted';
  const canComplete = processState === 'active';
  const canCancelAsAdmin = processState === 'accepted' && isAdmin;
  const canCancel = processState === 'active' && paymentCount <= 1;
  const isInactive = processState === 'completed' || processState === 'cancelled';
  const isExpired = processState === 'payment-expired';

  return (
    <Page title={intl.formatMessage({ id: 'SubscriptionPage.title' })}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar><TopbarContainer /></LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.container}>
            {isLoading && <div className={css.loadingBox}>Ładowanie…</div>}
            {error && <div className={css.errorBox}>{error.message}</div>}

            {subscription && (
              <div className={css.content}>
                <SubscriptionHero
                  listing={listing}
                  provider={provider}
                  subscription={subscription}
                  state={state}
                  intl={intl}
                  isProvider={isProvider}
                  isPickingUp={isPickingUp}
                  isCompleting={isCompleting}
                  onPickup={() => openConfirmation('pickup')}
                  onComplete={() => openConfirmation('complete')}
                  isCancelable={isCancelable}
                  onCancel={() => openConfirmation('cancel')}
                  isCancelling={isCancelling}
                  canPickup={canPickup}
                  canComplete={canComplete}
                  canCancel={canCancel || canCancelAsAdmin}
                />
                <div className={css.contentGrid}>
                  <InfoTile
                    title={intl.formatMessage({ id: 'SubscriptionPage.paymentTitle' })}
                  >
                    <PaymentDetails
                      subscription={subscription}
                      cardInfo={cardInfo}
                      nextPaymentDate={nextPaymentDate}
                      intl={intl}
                      onEdit={() => startUpdate()}
                      isStarting={isStarting}
                      isProvider={isProvider}
                    />
                  </InfoTile>
                  <InfoTile
                    title={intl.formatMessage({ id: 'SubscriptionPage.invoiceTitle' })}
                    headerAction={
                      !isInactive && false && (
                        <button
                          className={css.editBtn}
                          onClick={() => setIsInvoiceModalOpen(true)}
                        >
                          <FormattedMessage id="SubscriptionPage.editInvoiceBtn" />
                        </button>
                      )
                    }
                  >
                    <InvoiceDetails invoiceData={invoiceData} />
                  </InfoTile>
                  <InfoTile
                    title={intl.formatMessage({ id: "SubscriptionDeliveryDetails.title"})}
                  >
                    <SubscriptionDeliveryDetails subscription={subscription} />
                  </InfoTile>
                  {!isExpired && (
                    <InfoTile
                      title={intl.formatMessage({ id: 'SubscriptionPage.cancelTitle' })}
                    >
                      <CancellationNotice subscription={subscription} isProvider={isProvider}/>
                    </InfoTile>
                  )}
                  <InfoTile
                    title={intl.formatMessage({ id: 'SubscriptionPage.communicationTitle' }, { defaultValue: 'Komunikacja' })}
                    className={css.communicationSection}
                  >
                    <SubscriptionChat
                      messages={messages?.data?.data || []}
                      onSend={(txt) => sendMessageMutation.mutate(txt)}
                      isSending={sendMessageMutation.isLoading}
                      isProvider={isProvider}
                      intl={intl}
                    />
                  </InfoTile>
                  <InfoTile
                    title={intl.formatMessage({ id: 'SubscriptionHistory.title' })}
                    className={css.historySection}
                  >
                    <SubscriptionHistory
                      transitions={subscription?.attributes?.transitions}
                      documents={subscription?.attributes?.documents} // <--- Dodajemy propy
                      currency={subscription?.attributes?.monthlyPrice?.currency}
                      processState={subscription?.attributes?.processState}
                      unpaidSince={subscription?.attributes?.unpaidSince}
                      nextPaymentAttemptAt={subscription?.attributes?.nextPaymentAttemptAt}
                      updatedAt={subscription?.attributes?.updatedAt}
                      intl={intl}
                    />
                  </InfoTile>

                  {isModalOpen && setupIntentSecret && (
                    <div className={css.modalOverlay} onClick={() => setIsModalOpen(false)}>
                      <div className={css.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={css.modalHeader}>
                          <h3 className={css.modalTitle}>Nowa metoda płatności</h3>
                          <button className={css.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
                        </div>

                        <div className={css.modalBody}>
                          <ChangeCardForm
                            setupIntentSecret={setupIntentSecret}
                            user={subscription.attributes}
                            isSyncing={syncMutation.isLoading}
                            onCancel={() => setIsModalOpen(false)}
                            onSave={() => syncMutation.mutate()}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {isInvoiceModalOpen && (
                    <div className={css.modalOverlay} onClick={() => setIsInvoiceModalOpen(false)}>
                      <div className={css.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={css.modalHeader}>
                          <h3 className={css.modalTitle}>Edytuj dane do faktury</h3>
                          <button className={css.closeBtn} onClick={() => setIsInvoiceModalOpen(false)}>×</button>
                        </div>
                        <div className={css.modalBody}>
                          <EditInvoiceForm
                            initialData={invoiceData}
                            isSaving={updateInvoiceMutation.isLoading}
                            onCancel={() => setIsInvoiceModalOpen(false)}
                            onSave={(data) => updateInvoiceMutation.mutate(data)}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <ConfirmationModal
                    isOpen={confirmationModal.isOpen}
                    actionType={confirmationModal.actionType}
                    onClose={closeConfirmation}
                    isLoading={isPickingUp || isCompleting || isCancelling}
                    onConfirm={(data) => {
                      if (confirmationModal.actionType === 'pickup') handlePickup();
                      if (confirmationModal.actionType === 'complete') handleComplete(data);
                      if (confirmationModal.actionType === 'cancel') handleCancel();
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter><Footer /></LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

export default compose(withRouter, injectIntl)(SubscriptionPageComponent);